import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PencilIcon } from "@heroicons/react/24/outline";
import { PencilLineIcon } from "lucide-react";
import { PlusIcon } from "@heroicons/react/24/outline";
import Card from "../../../components/ui/Card";
import HoverActionsMenu from "../../../components/HoverActionsMenu";
import ConfirmDialog from "../../../dialog/ConfirmDialog";
import OctagonAlertIcon from "../../../icons/OctagonAlertIcon";

interface Price {
  id: string;
  author: string;
  avatarUrl: string;
  timeLabel: string;
  title: string;
  titlePreview: string;
  description: string;
  descriptionPreview: string;
  icon?: React.ReactNode;
}

interface PricesSectionProps {
  prices?: Price[];
  onAddPrice?: () => void;
  onEditPrice?: (priceId: string) => void;
  onDeletePrice?: (priceId: string) => void;
}

const PriceCard: React.FC<{
  price: Price;
  onEdit?: () => void;
  onDelete?: () => void;
}> = ({ price, onEdit, onDelete }) => {
  return (
    <Card className="group relative overflow-hidden w-full h-full min-h-[120px]">
      {/* Hover Actions Menu */}
      <HoverActionsMenu
        onOpen={onEdit}
        onDelete={onDelete}
        className="!top-2 !right-2 !translate-y-0"
      />

      {/* Card Body */}
      <div className="flex flex-col gap-5 p-2">
        {/* Meta Section */}
        <div className="flex items-start gap-1 w-full">
          <div className="flex gap-1 items-center flex-1">
            {/* User Info */}
            <div className="flex gap-1 items-center">
              <img
                src={price.avatarUrl}
                alt={price.author}
                className="w-5 h-5 rounded-full flex-shrink-0"
              />
              <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{price.author}</p>
            </div>
            <p className="text-xs text-gray-600 whitespace-nowrap">{price.timeLabel}</p>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex flex-col gap-2 w-full">
          {/* Title */}
          <div className="flex gap-1 items-start w-full" title={price.title}>
            <PencilLineIcon className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-semibold text-gray-900 flex-1">{price.titlePreview}</p>
          </div>

          {/* Description */}
          <div className="text-xs text-gray-600 text-wrap">{price.descriptionPreview}</div>
        </div>
      </div>
    </Card>
  );
};

export default function PricesSection({
  prices = [],
  onAddPrice,
  onEditPrice,
  onDeletePrice,
}: PricesSectionProps) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (priceId: string) => {
    setDeleteId(priceId);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId || !onDeletePrice) return;
    try {
      setDeleting(true);
      await onDeletePrice(deleteId);
      setConfirmOpen(false);
      setDeleteId(null);
    } catch (error) {
      // Error handling can be added here if needed
      console.error("Failed to delete price:", error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="w-full space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between pb-2">
          <h2 className="text-lg font-semibold text-gray-900">{t("Prices")}</h2>
          <button
            onClick={onAddPrice}
            className="inline-flex items-center gap-1.5 px-1.5 py-1.5 bg-white border border-gray-300 rounded-md shadow-xs hover:bg-gray-50 transition-colors"
          >
            <PlusIcon className="w-3 h-3 text-gray-900" />
            <span className="text-xs font-medium text-gray-900 whitespace-nowrap">
              {t("Add Price")}
            </span>
          </button>
        </div>

        {/* Prices Grid */}
        {prices.length > 0 && (
          <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-3 w-full">
            {prices.map((price) => (
              <PriceCard
                key={price.id}
                price={price}
                onEdit={() => onEditPrice?.(price.id)}
                onDelete={() => handleDeleteClick(price.id)}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {prices.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <PencilIcon className="w-8 h-8 text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">{t("No prices yet")}</p>
            <button
              onClick={onAddPrice}
              className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              {t("Add your first price")}
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        visible={confirmOpen}
        title={t("Delete price?")}
        description={t("This action cannot be undone. The price will be permanently deleted.")}
        alertIcon={<OctagonAlertIcon />}
        dangerBtn
        submitting={deleting}
        onClose={() => {
          if (!deleting) {
            setConfirmOpen(false);
            setDeleteId(null);
          }
        }}
        onSubmit={handleConfirmDelete}
        submitBtnLabel={t("Delete")}
      />
    </>
  );
}
